<template>
	<VModal :modalId="modalId" :title="_tr('users.editUser')" size="xl" :show-buttons="false">
		<div class="space-y-6 p-2 md:p-4">
			<section class="border-base-200/70 bg-base-100/90 rounded-2xl border p-4 shadow-sm backdrop-blur">
				<div class="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
					<div class="space-y-1">
						<p class="text-base-content/60 text-xs font-semibold tracking-[0.14em] uppercase">
							{{ _tr('users.user') }}
						</p>
						<div class="flex flex-wrap items-center gap-2">
							<h3 class="text-base-content text-2xl leading-tight font-semibold">
								{{ selectedUser.email || _tr('users.email') }}
							</h3>
							<span class="badge badge-soft" :class="isNewUser ? 'badge-primary' : 'badge-outline'">
								{{ isNewUser ? _tr('users.userStatusDraft') : _tr('users.userStatusSaved') }}
							</span>
						</div>
						<p class="text-base-content/70 text-sm">{{ displayName }}</p>
					</div>
					<div class="flex flex-wrap items-center gap-2">
						<span class="badge badge-primary badge-soft capitalize">{{ roleLabel }}</span>
						<span class="badge badge-ghost">{{ _tr('users.projectsBadge', { count: projectCount }) }}</span>
					</div>
				</div>
				<!-- <div class="mt-4 grid gap-3 sm:grid-cols-3">
					<div class="border-base-200/80 bg-base-200/50 rounded-xl border px-3 py-2">
						<p class="text-base-content/60 text-xs tracking-wide uppercase">User ID</p>
						<p class="text-base-content truncate text-sm font-semibold">
							{{ selectedUser._id || 'Not saved yet' }}
						</p>
					</div>
					<div class="border-base-200/80 bg-base-200/50 rounded-xl border px-3 py-2">
						<p class="text-base-content/60 text-xs tracking-wide uppercase">{{ _tr('users.telephone') }}</p>
						<p class="text-base-content text-sm font-semibold">
							{{ selectedUser.telephone || 'Not set' }}
						</p>
					</div>
					<div class="border-base-200/80 bg-base-200/50 rounded-xl border px-3 py-2">
						<p class="text-base-content/60 text-xs tracking-wide uppercase">
							{{ _tr('users.assignedProjects') }}
						</p>
						<p class="text-base-content text-sm font-semibold">{{ projectCount }} selected</p>
					</div>
				</div> -->
			</section>

			<form class="space-y-6" @submit.prevent="">
				<section class="border-base-200/70 bg-base-100/90 rounded-2xl border p-4 shadow-sm">
					<div class="flex items-center justify-between gap-2 pb-1">
						<h4 class="text-base-content text-base font-semibold">{{ _tr('users.accountDetailsTitle') }}</h4>
						<span class="text-base-content/60 text-xs">
							{{
								isNewUser
									? _tr('users.accountDetailsSubtitleNew')
									: _tr('users.accountDetailsSubtitleExisting')
							}}
						</span>
					</div>
					<div class="grid gap-4 md:grid-cols-2">
						<label class="flex flex-col gap-1.5">
							<span class="text-base-content/70 text-sm font-medium">{{ _tr('users.email') }}</span>
							<input
								class="input input-bordered w-full"
								v-model="selectedUser.email"
								type="email"
								:placeholder="_tr('users.email')"
								:disabled="selectedUser._id"
								:aria-label="_tr('users.email')" />
						</label>

						<label class="flex flex-col gap-1.5" v-if="!selectedUser._id">
							<span class="text-base-content/70 text-sm font-medium">{{ _tr('users.password') }}</span>
							<input
								class="input input-bordered w-full"
								v-model="selectedUser.password"
								type="text"
								:placeholder="_tr('users.password')"
								:aria-label="_tr('users.password')" />
						</label>

						<label class="flex flex-col gap-1.5">
							<span class="text-base-content/70 text-sm font-medium">{{ _tr('users.firstname') }}</span>
							<input
								class="input input-bordered w-full"
								v-model="selectedUser.firstname"
								type="text"
								:placeholder="_tr('users.firstname')"
								:aria-label="_tr('users.firstname')" />
						</label>

						<label class="flex flex-col gap-1.5">
							<span class="text-base-content/70 text-sm font-medium">{{ _tr('users.lastname') }}</span>
							<input
								class="input input-bordered w-full"
								v-model="selectedUser.lastname"
								type="text"
								:placeholder="_tr('users.lastname')"
								:aria-label="_tr('users.lastname')" />
						</label>

						<label class="flex flex-col gap-1.5">
							<span class="text-base-content/70 text-sm font-medium">{{ _tr('users.telephone') }}</span>
							<input
								class="input input-bordered w-full"
								v-model="selectedUser.telephone"
								type="text"
								:placeholder="_tr('users.telephone')"
								:aria-label="_tr('users.telephone')" />
						</label>

						<label class="flex flex-col gap-1.5">
							<span class="text-base-content/70 text-sm font-medium">{{ _tr('users.role') }}</span>
							<select
								class="select select-bordered w-full"
								v-model="selectedUser.role"
								:aria-label="_tr('users.role')">
								<option value="user">{{ _tr('users.roles.user') }}</option>
								<option value="admin">{{ _tr('users.roles.admin') }}</option>
								<option v-if="isDev" value="developer">{{ _tr('users.roles.developer') }}</option>
							</select>
						</label>
					</div>
				</section>

				<section
					v-if="selectedUser.role == 'user'"
					class="border-base-200/70 bg-base-100/90 rounded-2xl border p-4 shadow-sm">
					<div class="flex items-start justify-between gap-2">
						<div class="space-y-1">
							<h4 class="text-base-content text-base font-semibold">
								{{ _tr('users.assignedProjects') }}
							</h4>
							<p class="text-base-content/70 text-sm">{{ _tr('users.chooseProjects') }}</p>
						</div>
						<span class="badge badge-ghost">{{ _tr('users.projectsSelected', { count: projectCount }) }}</span>
					</div>

					<div class="mt-4 flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
						<label class="input input-bordered mt-3 flex items-center gap-2">
							<Search class="text-base-content/60" size="18" />
							<input
								class="grow bg-transparent text-sm outline-none"
								v-model="search"
								type="search"
								:placeholder="_tr('users.filterProjects')"
								:aria-label="_tr('users.filterProjects')" />
						</label>

						<!-- label+checkbox for archived projects -->
						<div class="mt-2">
							<label class="flex items-center gap-2">
								<input type="checkbox" v-model="showArchivedProjects" class="checkbox checkbox-sm" />
								<span class="text-base-content/70 text-sm">{{ _tr('users.showArchivedProjects') }}</span>
							</label>
						</div>
					</div>
					<div class="bg-base-200/50 mt-3 grid max-h-72 gap-2 overflow-auto rounded-xl p-2">
						<label
							v-for="pr in filteredProjects"
							:key="pr._id"
							class="hover:border-primary/40 hover:bg-primary/5 flex cursor-pointer items-start gap-3 rounded-lg border border-transparent px-3 py-2 transition"
							:class="{
								'border-primary bg-primary/5 ring-primary/30 ring-1': isProjectSelected(pr._id),
								'opacity-70': pr.isArchived,
							}">
							<input
								class="checkbox checkbox-sm mt-1"
								v-model="selectedUser.accessProjects"
								type="checkbox"
								:value="pr._id"
								:aria-label="pr.name" />
							<div class="flex grow items-start justify-between gap-3">
								<div class="space-y-1">
									<div class="flex flex-wrap items-center gap-2">
										<span class="badge badge-primary badge-soft">{{ pr.projectNumber }}</span>
										<span class="text-base-content font-semibold">
											{{ pr.shortName || pr.name }}
										</span>
									</div>
									<p class="text-base-content/70 text-sm leading-snug">
										{{ pr.projectNumber }} {{ pr.name }}
									</p>
								</div>
								<span v-if="pr.isArchived" class="badge badge-error badge-soft h-min">
									{{ _tr('users.archived') }}
								</span>
							</div>
						</label>

						<div
							v-if="!filteredProjects.length"
							class="border-base-200/80 bg-base-200/50 text-base-content/70 rounded-lg border border-dashed px-3 py-4 text-center text-sm">
							{{ _tr('users.noProjectsMatch') }}
						</div>
					</div>
				</section>
			</form>
		</div>

		<template #footer v-if="!selectedUser._id">
			<div class="border-base-200/70 bg-base-100/90 flex w-full items-center justify-end gap-2 border-t p-3">
				<div class="flex items-center gap-2">
					<button
						class="btn btn-primary"
						:class="{ 'btn-disabled': !hasChanges }"
						:disabled="!hasChanges || isLoading"
						@click="addUser()">
							<span class="w-6">
								<span class="loading loading-spinner loading-sm" v-if="isLoadingAdd"></span>
								<Save size="20" v-else />
							</span>
							<span class="bold">{{ _tr('users.addUser') }}</span>
						</button>
					</div>
				</div>
			</template>
		<template #footer v-else>
			<div class="border-base-200/70 bg-base-100/90 flex w-full items-center justify-between gap-2 border-t p-3">
				<ConfirmButton text="" title="" @confirm="deleteUser(selectedUser._id)">
					<button class="btn btn-soft btn-error" :class="{ 'btn-disabled': isLoading }" :disabled="isLoading">
						<span class="w-6">
							<span class="loading loading-spinner loading-sm" v-if="isLoadingDelete"></span>
							<Trash2 size="20" v-else />
						</span>
						{{ _tr('users.deleteUser') }}
					</button>
				</ConfirmButton>

				<div class="flex items-center gap-2">
					<button
						class="btn btn-soft btn-secondary"
						:class="{ 'btn-disabled': !hasChanges }"
						:disabled="!hasChanges || isLoading"
						@click="updateUser()">
							<span class="w-6">
								<span class="loading loading-spinner loading-sm" v-if="isLoadingUpdate"></span>
								<Save size="20" v-else />
							</span>
							<span class="bold">{{ _tr('users.save') }}</span>
						</button>
					</div>
				</div>
			</template>
	</VModal>
</template>

<script setup lang="ts">
	import { ref, watch, computed } from 'vue'
	import { useI18n } from 'vue-i18n'
	import VModal from '@/components/VModalFinal.vue'
	import { detailedDiff } from 'deep-object-diff'
	import { Save, Search, Trash2 } from 'lucide-vue-next'
	import http from '@/libs/http'
	import { useToast } from 'vue-toastification'
	import { useBaseStore } from '@/stores/base.store'
	import { useVfm } from 'vue-final-modal'
	import ConfirmButton from '@/components/ConfirmButton.vue'
	import { useAuthStore } from '@/stores/auth.store'
	import { _translations } from './_translations'

	const modalId = 'modal-edit-user'
	const { t: _tr } = useI18n(_translations)
	const vfm = useVfm()
	const toast = useToast()

	interface Props {
		selectedUser?: any
	}
	const props = withDefaults(defineProps<Props>(), {
		selectedUser: null,
	})
	const authStore = useAuthStore()
	const search = ref('')
	const showArchivedProjects = ref(false)
	const isLoadingUpdate = ref(false)
	const isLoadingDelete = ref(false)
	const isLoadingAdd = ref(false)
	const isDev = computed(() => authStore.isDev)

	const buildEmptyUser = (withPassword = true) => ({
		email: '',
		firstname: '',
		lastname: '',
		telephone: '',
		role: 'user',
		accessProjects: [] as string[],
		...(withPassword ? { password: '' } : {}),
	})

	const selectedUser = ref<any>(buildEmptyUser())

	const baseStore = useBaseStore()

	const projects = computed(() => baseStore.projects)
	const filteredProjects = computed(() => {
		const term = search.value.toLowerCase()
		return projects.value
			.slice()
			.filter(
				(u: any) =>
					(showArchivedProjects.value ? true : !u.isArchived) &&
					JSON.stringify([u._id, u.projectNumber, u.name, u.shortName, u.state, u.comment])
						.toLowerCase()
						.includes(term),
			)
			.sort((a: any, b: any) => (a.projectNumber || '').localeCompare(b.projectNumber || ''))
	})
	const changes = computed(() => detailedDiff(props.selectedUser || {}, selectedUser.value || {}))
	const hasChanges = computed(() => Object.values(changes.value).some((values) => Object.keys(values).length > 0))
	const isLoading = computed(() => isLoadingUpdate.value || isLoadingDelete.value || isLoadingAdd.value)
	const projectCount = computed(() => (selectedUser.value?.accessProjects || []).length)
	const displayName = computed(() => {
		const first = (selectedUser.value?.firstname || '').trim()
		const last = (selectedUser.value?.lastname || '').trim()
		return first || last ? `${first} ${last}`.trim() : _tr('users.nameNotSet')
	})
	const roleLabel = computed(
		() => _tr(`users.roles.${selectedUser.value?.role || 'user'}`) || selectedUser.value?.role || 'user',
	)
	const isNewUser = computed(() => !selectedUser.value?._id)

	const isProjectSelected = (projectId: string) => (selectedUser.value?.accessProjects || []).includes(projectId)

	watch(
		() => props.selectedUser,
		(value) => {
			if (value) {
				selectedUser.value = JSON.parse(
					JSON.stringify({
						...buildEmptyUser(false),
						...value,
						accessProjects: value.accessProjects || [],
					}),
				)
			} else {
				selectedUser.value = buildEmptyUser()
			}
		},
		{ immediate: true },
	)

	const addUser = async () => {
		try {
			if (isLoading.value || selectedUser.value._id) return
			isLoadingAdd.value = true
			await http.postAuth('/api/v1/users', selectedUser.value)
			await baseStore.fetchUsers()
			toast.success(_tr('users.successUserAdded'))
			vfm.close(modalId)
		} catch (error: any) {
			console.error(error)
			toast.error(error.message)
		} finally {
			isLoadingAdd.value = false
		}
	}

	const updateUser = async () => {
		try {
			if (isLoading.value) return
			isLoadingUpdate.value = true
			await http.putAuth(`/api/v1/users/${selectedUser.value._id}`, selectedUser.value)
			await baseStore.fetchUsers()
			toast.success(_tr('users.successUserUpdated'))
			vfm.close(modalId)
		} catch (error: any) {
			console.error(error)
			toast.error(error.message)
		} finally {
			isLoadingUpdate.value = false
		}
	}

	const deleteUser = async (userId: string) => {
		try {
			if (isLoading.value) return
			isLoadingDelete.value = true
			await http.deleteAuth(`/api/v1/users/${userId}`)
			await baseStore.fetchUsers()
			toast.success(_tr('users.successUserDeleted'))
			vfm.close(modalId)
		} catch (error: any) {
			console.error(error)
			toast.error(error.message)
		} finally {
			isLoadingDelete.value = false
		}
	}
</script>
